/**
 * Developer-only race-engine inspection client (backend/api/routes/race_engine_inspect.py).
 * Enabled with ?inspect=1 on a replay URL, in development builds or when REACT_APP_RACE_INSPECTOR=1.
 */
import { API } from "../../lib/api";

const BASE = `${API}/race-engine/inspect`;

async function getJson(url, timeoutMs = 120000) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const response = await fetch(url, { signal: controller.signal, headers: { Accept: "application/json" } });
        if (!response.ok) {
            const error = new Error(`HTTP ${response.status} for ${url}`);
            error.status = response.status;
            throw error;
        }
        return await response.json();
    } finally {
        clearTimeout(timer);
    }
}

export const inspectorApi = {
    summary: (sessionKey) => getJson(`${BASE}/sessions/${sessionKey}`),
    samples: (sessionKey, t0, t1) => getJson(`${BASE}/sessions/${sessionKey}/samples?t0=${encodeURIComponent(t0)}&t1=${encodeURIComponent(t1)}`, 60000),
    serverSnapshot: (sessionKey, t) => getJson(`${API}/race-engine/sessions/${sessionKey}/replay/snapshot?t=${encodeURIComponent(t)}`, 30000),
};

export function inspectorAllowed(search) {
    const params = new URLSearchParams(search || "");
    if (params.get("inspect") !== "1") return false;
    return process.env.NODE_ENV !== "production" || process.env.REACT_APP_RACE_INSPECTOR === "1";
}

/** Inspection sample row: [session_seconds, x, y, verdict, progress, branch, quality, lateral_m, speed_mps] (layout frame, 0.1 m). */
export const SAMPLE = { S: 0, X: 1, Y: 2, VERDICT: 3, PROGRESS: 4, BRANCH: 5, QUALITY: 6, LATERAL: 7, SPEED: 8 };
export const VERDICTS = ["published", "thinned", "suspect", "late", "unprojected", "off_layout", "backstep"];
export const ACCEPTED = (row) => row[SAMPLE.VERDICT] <= 1;

/** Layer presets: a clean viewing mode and a diagnostic overlay; both draw through the same renderer and race state. */
export const LAYER_PRESETS = {
    clean: { raw: false, projected: false, path: false, lines: true, pit: true, trail: true, rejected: false, grid: false },
    diagnostic: { raw: true, projected: true, path: true, lines: true, pit: true, trail: true, rejected: true, grid: true },
};

export function presetOf(toggles) {
    return Object.entries(LAYER_PRESETS).find(([, preset]) => Object.keys(preset).every((key) => !!toggles[key] === preset[key]))?.[0] || "custom";
}

/**
 * Measured lineup vs an official order, qualified the same way as the backend's start_order_verdict: ``exact``,
 * ``adjacent_unresolved`` (only swapped neighbours standing less than one slot spacing apart — the measurement cannot
 * tell them apart, so it is not a verification), ``differs``, or ``no_official_order``.
 */
export function lineupVerdict(ranked, spacingM) {
    // ranked: [{entry_id, acronym, expected (official position), measured (grid slot), to_line_m}]
    if (ranked.length < 2) return { kind: "no_official_order", text: "No official order to compare against.", pairs: [] };
    const byExpected = [...ranked].sort((a, b) => a.expected - b.expected);
    const expectedSlot = new Map(byExpected.map((item, index) => [item.entry_id, index + 1]));
    const mismatches = ranked.filter((item) => item.measured !== expectedSlot.get(item.entry_id));
    if (!mismatches.length) return { kind: "exact", text: `Measured along-track order equals the official order for all ${ranked.length} grid cars (left/right stagger not measured).`, pairs: [] };
    const spacing = spacingM || 8;
    const pairs = [];
    const seen = new Set();
    for (const item of mismatches) {
        if (seen.has(item.entry_id)) continue;
        const partner = mismatches.find((other) => other.entry_id !== item.entry_id && other.measured === expectedSlot.get(item.entry_id) && expectedSlot.get(other.entry_id) === item.measured);
        if (!partner || Math.abs(partner.expected - item.expected) !== 1) return { kind: "differs", text: `Measured along-track order differs from the official order for ${mismatches.length} grid car(s); the measurement does not confirm it.`, pairs: [] };
        seen.add(item.entry_id);
        seen.add(partner.entry_id);
        pairs.push({ entries: [item.acronym, partner.acronym], gapM: Math.round(Math.abs((item.to_line_m || 0) - (partner.to_line_m || 0)) * 10) / 10 });
    }
    if (pairs.some((pair) => pair.gapM > spacing)) return { kind: "differs", text: "Measured along-track order differs from the official order by more than one slot spacing; the measurement does not confirm it.", pairs };
    return { kind: "adjacent_unresolved", text: `Official order available; measured placement cannot resolve these adjacent entries: ${pairs.map((pair) => `${pair.entries[0]}/${pair.entries[1]} (${pair.gapM} m apart)`).join("; ")} (one slot spacing is ${Math.round(spacing * 10) / 10} m). Not an exact verification.`, pairs };
}

/** Grid slots form one cluster ~8 m apart; cars standing outside it (pit lane, far ahead/behind) are not on the grid. */
export function gridCluster(slots) {
    const track = slots.filter((slot) => slot.branch === "track");
    const groups = [];
    track.forEach((slot) => {
        const group = groups[groups.length - 1];
        if (group && group[group.length - 1].to_line_m - slot.to_line_m <= 26) group.push(slot); // one missing slot leaves ~16-21 m
        else groups.push([slot]);
    });
    const main = groups.reduce((best, group) => (group.length > (best?.length || 0) ? group : best), null) || [];
    const ids = new Set(main.map((slot) => slot.entry_id));
    return { main, outside: slots.filter((slot) => !ids.has(slot.entry_id)) };
}
